import { clearLastRoute } from '../ui/lastRouteStorage';
import { clearCobrarSearchSeed } from '../billing/cobrarSearchSeed';
import { clearPendingCustomerOpen } from '../ui/pendingCustomerOpen';
import { clearRecordOpeners } from '../ui/recordOpeners';
import { closeSecurityEventsModal } from '../ui/securityEventsModalBus';
import { clearCachedMfa } from './mfaStatusCache';
import { clearMfaSetupIntent } from './mfaSetupIntent';
import { clearWorkerTransferNameCache } from '../worker/workerTransferNotice';
import { clearMfaResetNoteCache } from '../../components/auth/mfa/mfaResetNoteCache';
import { clearTotpFactorsSnapshot } from './totpFactorsSnapshot';
import { clearCreditsStore } from '../billing/customerCreditsStore';
import { resetDashboardEntrance } from '../ui/dashboardEntranceState';
import { clearRecentlySent } from '../invoice/invoiceSentSignal';
import { resetFirstRunHint } from '../onboarding/firstRunHint';
import { resetSplashGlideSession } from '../../ui/splash/splashGlide';
import { clearAllDashboardCache } from '../cache/dashboardCache';
import { clearDashboardActivityStore } from '../dashboard/dashboardActivityStore';
import { clearSecurityEventsMemory } from '../../services/securityEvents';
import { SECURITY_ALERT_RECEIPTS_PREFIX } from '../notices/securityAlertReceipts';
import { clearMfaStatusPrefetch } from './mfaStatusPrefetch';
import { clearSignInPrefetch } from '../dashboard/signInPrefetch';
import { clearAccountSessionsStore } from '../../services/accountSessionsStore';
import { clearPlatformSessionMemory } from '../platform/platformSessionStore';
import { invalidateAllSharedInFlights } from '../sharedInFlight';
import { clearPlatformPeopleCache } from '../../services/platformPeopleResolver';
import { clearPlatformViewCache } from '../platform/platformViewCache';
import { clearMemberLastSignInStore } from '../team/memberLastSignInStore';
import { clearPendingLockOpen } from '../company/officeLockDialogBus';
import { COMPANY_ACCESS_GREETED_PREFIX } from '../company/companyAccessGreetingReceipts';
import { SUBSCRIBE_CELEBRATED_PREFIX, SUBSCRIBE_PENDING_PREFIX } from '../billing/subscribeCheckoutMarker';
import { TEAMMATE_JOINED_GREETED_PREFIX } from '../team/teammateJoinedReceipts';
import { PORTAL_RETURN_PREFIX } from '../billing/portalReturnMarker';
import { LAST_KNOWN_ACCESS_PREFIX } from '../company/lastKnownAccessLevel';
import { BRIEFING_STORAGE_PREFIX } from '../ai/briefingStorage';
import { ALL_DONE_SEEN_PREFIX } from '../worker/allDoneSeen';
import { resetOpenPlanBillingForSignOut } from '../company/openPlanBilling';
import { purgeLegacyBulkRunIds } from '../invoice/bulkRunId';
import { clearRemovedInvoiceIds } from '../invoice/removedInvoiceIds';
import { forgetThemeOwnerForSignOut } from '../../ui/theme/themeController';
import { resetOverlayDismissPending } from '../ui/overlayDismissBus';

// RD-PERF-003: every dashboard memory cache (main data, recent activity,
// security events, MFA prefetch) is per-user memory — one helper so the two
// sign-out paths below can never drift apart.
function clearDashboardMemory() {
  clearAllDashboardCache();
  clearDashboardActivityStore();
  clearSecurityEventsMemory();
  clearMfaStatusPrefetch();
  clearSignInPrefetch(); // RD-LUI-003: a pre-fetched dashboard bundle is per-user memory
  clearAccountSessionsStore(); // device names / masked IP / location are per-user memory
  clearPlatformSessionMemory(); // platform home lists, own profile/timezone, login-failure count
  clearMemberLastSignInStore(); // Team tab last sign-in times (RD-UI-045)
  invalidateAllSharedInFlights(); // RD-PERF-004: no new session joins a read started under the old one
  clearPlatformViewCache(); // KAN-729: platform dashboard reads + company activity are per-user memory
  clearPlatformPeopleCache(); // names/roles resolved for the Platform logs (KAN-706)
  clearPendingLockOpen(); // KAN-827: a held read-only dialog request must not reach the next session
  resetOpenPlanBillingForSignOut(); // KAN-204: a pending Open Plan & billing request must not navigate the next session
  resetOverlayDismissPending();
  clearRemovedInvoiceIds(); // KAN-1121: ids deleted this session are per-user memory
}

// RD-BILL-007 — login_success audit dedupe window marker. Kept here (not
// re-exported elsewhere) since AuthContext.js is the only reader.
export const AUDIT_LOGIN_KEY = 'cv_audit_login_marker';

// Retired per-user dismissal set for the old History → Alerts subtab
// (replaced by per-user security-alert receipts — see
// src/utils/notices/securityAlertReceipts.js). The mechanism is gone, so nothing
// writes this key anymore, but a browser that used it before the retirement
// may still be holding it; sweep it on sign-out so it doesn't sit in
// localStorage forever. Not tied to a single tenantId/userId (the old key
// shape was `${PREFIX}${tenantId}_${userId}`), so this clears every matching
// key regardless of whose it was.
const LEGACY_ALERT_DISMISSALS_PREFIX = 'cv_activity_alert_dismissals_';

// The current per-user receipts cache (`cvx-security-alert-reviewed-v1:<userId>`)
// is swept the same way: the next person on this browser must never inherit it.
function clearLegacyAlertDismissals() {
  try {
    const stale = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && (key.startsWith(LEGACY_ALERT_DISMISSALS_PREFIX) || key.startsWith(SECURITY_ALERT_RECEIPTS_PREFIX)
        || key.startsWith(COMPANY_ACCESS_GREETED_PREFIX) || key.startsWith(LAST_KNOWN_ACCESS_PREFIX)
        || key.startsWith(SUBSCRIBE_CELEBRATED_PREFIX) || key.startsWith(SUBSCRIBE_PENDING_PREFIX)
        || key.startsWith(PORTAL_RETURN_PREFIX) || key.startsWith(TEAMMATE_JOINED_GREETED_PREFIX)
        // The daily AI briefing names customers and money (KAN-946 security review).
        || key.startsWith(BRIEFING_STORAGE_PREFIX)
        || key.startsWith(ALL_DONE_SEEN_PREFIX))) stale.push(key);
    }
    stale.forEach((key) => window.localStorage.removeItem(key));
  } catch { /* ignore */ }
}

/**
 * Per-device, per-user state that must not leak to the next user on a shared
 * browser: last route, cached MFA hint, a pending "open the 2FA stepper"
 * intent, the audit-login dedupe marker, the worker route-change name cache
 * (customer PII), the prefetched MFA reset-note cache, the TOTP factor
 * snapshot, and any stale legacy alert dismissal keys.
 *
 * Extracted from AuthContext.js's signOut() so that file stays under the
 * 500-line cap. Runs on the explicit signOut() path only — expiry / revoke /
 * cross-tab sign-outs never reach signOut(); the SIGNED_OUT listener runs
 * `clearSignedOutMemoryState()` below instead.
 */
export function clearPerUserDeviceState() {
  resetFirstRunHint();
  clearLastRoute();
  clearCobrarSearchSeed();
  clearPendingCustomerOpen();
  clearRecordOpeners();
  closeSecurityEventsModal();
  clearCachedMfa();
  clearMfaSetupIntent();
  try { window.localStorage.removeItem(AUDIT_LOGIN_KEY); } catch { /* ignore */ }
  clearWorkerTransferNameCache();
  clearLegacyAlertDismissals();
  clearMfaResetNoteCache();
  clearTotpFactorsSnapshot();
  clearCreditsStore(); // RD-CX-022: tenant ledger is per-user memory
  clearDashboardMemory();
  // Next sign-in plays the dashboard entrance once again.
  resetDashboardEntrance();
  // KAN-562: no splash-glide clone, timer or landed DOM node outlives the session.
  resetSplashGlideSession();
  clearRecentlySent();
  purgeLegacyBulkRunIds(); // KAN-1028: older builds persisted bulk run ids
  forgetThemeOwnerForSignOut(); // KAN-304: the theme stays (no switch at sign-in); only its owner id is per-user state
}

/**
 * SIGNED_OUT listener subset (expiry / revoke / cross-tab — the paths that
 * never reach signOut()): the in-memory per-user caches that would otherwise
 * outlive the session until a reload. Worker route-change names (customer
 * PII), the MFA reset note, and the TOTP factor snapshot (2026-09-27 security
 * review — both MFA caches were only cleared on the explicit signOut() path).
 */
export function clearSignedOutMemoryState() {
  resetFirstRunHint();
  clearWorkerTransferNameCache();
  clearMfaResetNoteCache();
  clearTotpFactorsSnapshot();
  clearCreditsStore();
  clearDashboardMemory(); // expiry / revoke / cross-tab never reach signOut()
  clearCobrarSearchSeed(); // a customer name seeded for Cobrar must not outlive the session
  resetSplashGlideSession();
  clearPendingCustomerOpen();
  clearRecordOpeners();
  closeSecurityEventsModal();
  forgetThemeOwnerForSignOut(); // KAN-304: expiry / revoke forget the owner id too
}

export default clearPerUserDeviceState;
